import { copsAuthenticatorConstants as constants } from 'utils/app-configs';
import { getToken, login } from 'utils/cops-authenticator';
import { getLocale } from 'utils/i18n';
import { errorTypes, publishSteps } from './app-configs';
import { getCachedServiceCenter } from './service-center';

const getUrl = (endpoint, options) => {
  const finalUrl = options.api || API_ENDPOINT;
  if (endpoint.charAt(0) === '/') {
    return encodeURI(`${finalUrl}${endpoint}`);
  }
  return encodeURI(`${finalUrl}/${endpoint}`);
};

const getErrorObject = ({
  actions,
  message,
  errors,
  status,
  type,
  title,
  reference,
  submessage,
  isWarning = false,
  sections,
  options,
  endpoint,
}) => ({
  error: {
    actions,
    type,
    status,
    title,
    reference,
    message: message || options.errorMessage || '',
    submessage,
    errors: errors || [],
    url: getUrl(endpoint, options),
    retryAction: options.retryAction,
    isWarning,
    sections,
  },
});

const getMultiLineErrorObject = ({
  actions,
  message,
  errors,
  status,
  type,
  title,
  reference,
  multiLineRetriggerInfo,
  needsPoNumber,
  notFoundSerialNumbers,
  notFoundFunctionalLocationNumbers,
  numberFound,
  numberNotFound,
  numberOfDuplicates,
  totalNumber,
  isWarning = false,
  sections,
  options,
  endpoint,
}) => ({
  error: {
    actions,
    type,
    status,
    title,
    reference,
    message: message || options.errorMessage || '',
    errors: errors || [],
    url: getUrl(endpoint, options),
    retryAction: options.retryAction,
    multiLineRetriggerInfo,
    needsPoNumber,
    notFoundSerialNumbers,
    notFoundFunctionalLocationNumbers,
    numberFound,
    numberNotFound,
    numberOfDuplicates,
    totalNumber,
    isWarning,
    sections,
  },
});

const getValidationErrorObject = ({
  actions,
  backupHistory,
  errors,
  status,
  isWarning = false,
}) => ({
  error: {
    actions,
    type: 'validationError',
    status,
    backupHistory,
    errors: errors || [],
    isWarning,
  },
});

const checkStatus = (response, options, endpoint) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  if (response.status === 409) {
    return response.json().then(res =>
      Promise.reject({
        ...getErrorObject({
          message: null,
          errors: null,
          status: response.status,
          options,
          endpoint,
        }),
        lock: res,
      }),
    );
  }

  // Only for businessExceptions
  if (response.status === 400) {
    return response.text().then((text) => {
      if (text) {
        const {
          actions,
          statusName,
          message,
          errors,
          type,
          title,
          backupHistory,
          reference,
          multiLineRetriggerInfo,
          needsPoNumber,
          notFoundSerialNumbers,
          notFoundFunctionalLocationNumbers,
          numberFound,
          numberNotFound,
          numberOfDuplicates,
          totalNumber,
          submessage,
          isWarning,
          sections,
        } = JSON.parse(
          text,
        );

        // Only for validation errors
        if (statusName === publishSteps.copsValidationError.name) {
          return Promise.reject(
            getValidationErrorObject({
              actions,
              backupHistory,
              errors,
              status: response.status,
              isWarning,
            }),
          );
        }
        if (type === errorTypes.multiLine) {
          return Promise.reject(
            getMultiLineErrorObject({
              actions,
              message,
              errors,
              reference,
              status: response.status,
              type,
              title,
              multiLineRetriggerInfo,
              needsPoNumber,
              notFoundSerialNumbers,
              notFoundFunctionalLocationNumbers,
              numberFound,
              numberNotFound,
              numberOfDuplicates,
              totalNumber,
              isWarning,
              sections,
              options,
              endpoint,
            }),
          );
        }
        return Promise.reject(
          getErrorObject({
            actions,
            message,
            errors,
            reference,
            status: response.status,
            type,
            title,
            submessage,
            isWarning,
            sections,
            options,
            endpoint,
          }),
        );
      }
      return Promise.reject(getErrorObject({
        status: response.status,
        options,
        endpoint,
      }));
    });
  }

  return response.text().then((text) => {
    if (text) {
      const { actions, message, errors, type, title, submessage, isWarning } = JSON.parse(text);
      return Promise.reject(
        getErrorObject({
          actions,
          message,
          errors,
          status: response.status,
          type,
          title,
          submessage,
          isWarning,
          options,
          endpoint,
        }),
      );
    }
    return Promise.reject(getErrorObject({
      status: response.status,
      options,
      endpoint,
      // Default edge case, should not happen but added just in case
      title: 'Something went wrong',
    }));
  });
};

const addCustomHeaders = (token, selectedServiceCenter, mergedOptions) => {
  const customHeaders = {
    Authorization: `Bearer ${token}`,
    'cops-scope': selectedServiceCenter,
  };

  mergedOptions.headers = {
    ...mergedOptions.headers,
    ...customHeaders,
  };

  return mergedOptions;
};

const handleAuthentication = mergedOptions => getToken().then(
  (token) => {
    if (token) {
      const selectedServiceCenter = getCachedServiceCenter();
      return Promise.resolve(addCustomHeaders(token, selectedServiceCenter, mergedOptions));
    }
    // Prevent calling the login twice at startup, as this will open two interactive windows.
    if (localStorage.getItem(`auth-${constants.ACCESS_TOKEN}`)) { login(); }
    return Promise.reject();
  },
  err => Promise.reject(err),
);

export default ({ endpoint, options = {} }) => {
  const defaultOptions = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Accept-Language': getLocale(),
      'Content-Language': JSON.parse(localStorage.getItem('definitionModeEnabled')) ?
        'definition' :
        localStorage.getItem('locale'),
    },
    credentials: 'same-origin',
  };
  if (!options.disableContentType) {
    defaultOptions.headers['Content-Type'] = 'application/json';
  }
  if (options.acceptType) {
    defaultOptions.headers.Accept = options.acceptType;
  }
  if (options.contentType) {
    defaultOptions.headers['Content-Type'] = options.contentType;
  }

  const mergedOptions = { ...defaultOptions, ...options };

  if (defaultOptions.headers?.Accept === 'text/csv') {
    let headers = '';
    return handleAuthentication(mergedOptions)
      .then(opts => fetch(getUrl(endpoint, options), opts))
      .then(r => checkStatus(r, options, endpoint))
      .then((res) => {
        headers = res.headers.get('Content-Disposition');
        return res.text();
      })
      .then(data => ({
        headers,
        text: data,
      }))
      .catch((err) => {
        if (err.error) {
          return Promise.reject(err);
        }
        return Promise.reject({
          error: {
            status: 'FETCH_ERROR',
            message: options.errorMessage || 'Failed to fetch',
            retryAction: options.retryAction,
            url: getUrl(endpoint, options),
          },
        });
      });
  }

  return handleAuthentication(mergedOptions)
    .then(opts => fetch(getUrl(endpoint, options), opts))
    .then(r => checkStatus(r, options, endpoint))
    .then((res) => {
      if (res.status === 204 || res.status === 202 || res.status === 205 || res.status === 304) {
        return res;
      }
      return res.json();
    })
    .catch((err) => {
      if (err.error) {
        return Promise.reject(err);
      }
      return Promise.reject({
        error: {
          status: 'FETCH_ERROR',
          message: options.errorMessage || 'Failed to fetch',
          retryAction: options.retryAction,
          url: getUrl(endpoint, options),
        },
      });
    });
};
